
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { format, parseISO, isPast } from "date-fns";
import { 
  Calendar, 
  CreditCard, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  DollarSign
} from "lucide-react";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SubscriptionType, BookingType } from "@/lib/supabase/types";
import { fetchSubscriptionBookings, createSubscription } from "@/lib/supabase";
import { toast } from "sonner";
import { PaymentInformationSection } from "@/components/booking/PaymentInformationSection";

interface UserSubscriptionsProps {
  userId: string;
  subscriptions: SubscriptionType[];
  onCreateSubscription: () => void;
  onRefreshSubscriptions: () => void;
}

export const UserSubscriptions: React.FC<UserSubscriptionsProps> = ({ 
  userId, 
  subscriptions, 
  onCreateSubscription,
  onRefreshSubscriptions 
}) => {
  const { t } = useTranslation();
  const [subscriptionBookings, setSubscriptionBookings] = useState<{ [key: string]: BookingType[] }>({});
  const [expandedSubscriptions, setExpandedSubscriptions] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const fetchAllSubscriptionBookings = async () => {
      const bookingsMap: { [key: string]: BookingType[] } = {};
      
      for (const subscription of subscriptions) {
        try {
          const bookings = await fetchSubscriptionBookings(subscription.id);
          bookingsMap[subscription.id] = bookings;
        } catch (error) {
          console.error(`Error fetching bookings for subscription ${subscription.id}:`, error);
        }
      }
      
      setSubscriptionBookings(bookingsMap);
    };
    
    if (subscriptions.length > 0) {
      fetchAllSubscriptionBookings();
    }
  }, [subscriptions]);
  
  const handleToggleExpand = (subscriptionId: string) => {
    setExpandedSubscriptions(prev => 
      prev.includes(subscriptionId)
        ? prev.filter(id => id !== subscriptionId)
        : [...prev, subscriptionId]
    );
  };
  
  const handleCreateSubscription = async () => {
    if (!userId) return;
    
    setIsCreating(true);
    
    try {
      const result = await createSubscription(userId);
      
      if (result.success) {
        toast.success(t("dashboard.subscriptions.createdTitle"), {
          description: t("dashboard.subscriptions.createdDesc"),
        });
        onRefreshSubscriptions();
      } else {
        toast.error(t("dashboard.subscriptions.createErrorTitle"), {
          description: result.error || t("dashboard.subscriptions.createErrorDesc"),
        });
      }
    } catch (error) {
      console.error("Error creating subscription:", error);
      toast.error(t("dashboard.subscriptions.createErrorTitle"), {
        description: t("dashboard.subscriptions.unexpectedError"),
      });
    } finally {
      setIsCreating(false);
    }
  };
  
  const hasActiveSubscription = subscriptions.some(sub => 
    sub.active && sub.remainingClasses > 0 && !isPast(new Date(sub.expirationDate))
  );

  const getPaymentStatusBadge = (paymentStatus: string | undefined) => {
    switch (paymentStatus) {
      case 'paid':
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <DollarSign className="h-3 w-3 mr-1" />
            {t("dashboard.bookings.paid")}
          </Badge>
        );
      case 'nptr':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 hover:bg-red-100">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {t("dashboard.bookings.nptr") || "NPTR"}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {t("dashboard.bookings.paymentPending")}
          </Badge>
        );
    }
  };
  
  // Check if there are any subscriptions with pending payment
  const hasPendingPayments = subscriptions.some(sub => sub.payment_status === 'pending');
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="text-xl font-display font-medium">{t('dashboard.subscriptions.title')}</h2>
        <Button 
          variant="default" 
          onClick={handleCreateSubscription}
          disabled={isCreating}
        >
          <CreditCard className="h-4 w-4 mr-2" />
          {isCreating ? t('dashboard.subscriptions.purchasing') : t('dashboard.subscriptions.purchase')}
        </Button>
      </div>
      
      {subscriptions.length === 0 ? (
        <div className="text-center py-8 bg-card border border-border rounded-xl">
          <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
            <CreditCard className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-medium mb-2">{t('dashboard.subscriptions.none')}</h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-6">
            {t('dashboard.subscriptions.noneDesc')}
          </p>
          <Button onClick={handleCreateSubscription} disabled={isCreating}>
            {isCreating ? t('dashboard.subscriptions.purchasing') : t('dashboard.subscriptions.purchase')}
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {subscriptions.map((subscription, index) => {
            const isExpanded = expandedSubscriptions.includes(subscription.id);
            const isExpired = isPast(new Date(subscription.expirationDate));
            const bookings = subscriptionBookings[subscription.id] || [];
            const paymentStatus = subscription.payment_status || 'pending';
            const isPaid = paymentStatus === 'paid';
            
            return (
              <motion.div
                key={subscription.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-card border border-border rounded-xl overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-display font-medium">{t('dashboard.subscriptions.potterySubscription')}</h3>
                        {subscription.active && !isExpired ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-200">{t('dashboard.subscriptions.active')}</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-100 text-gray-800 hover:bg-gray-200">{t('dashboard.subscriptions.inactive')}</Badge>
                        )}
                        {getPaymentStatusBadge(paymentStatus)}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('dashboard.subscriptions.purchased')} {format(parseISO(subscription.purchaseDate), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                      <div className="flex items-center gap-1.5 text-sm font-medium">
                        <span className="text-2xl font-display">{subscription.remainingClasses}</span> 
                        <div className="flex flex-col">
                          <span>{t('dashboard.subscriptions.remaining')}</span>
                          <span className="text-xs text-muted-foreground">{t('dashboard.subscriptions.classesRemaining')}</span>
                        </div>
                        <span className="mx-1 text-muted-foreground">/</span>
                        <span>{subscription.totalClasses} {t('dashboard.subscriptions.total')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>
                        {t('dashboard.subscriptions.expires')}: {format(parseISO(subscription.expirationDate), 'MMM d, yyyy')}
                        {isExpired && (
                          <span className="ml-2 text-red-500 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> {t('dashboard.subscriptions.expired')}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  
                  {!isPaid && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-md">
                      <p className="text-sm text-amber-800 flex items-center">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        <span>
                          <strong>{t('dashboard.subscriptions.paymentPendingTitle')}:</strong> {t('dashboard.subscriptions.paymentPendingDesc')}
                        </span>
                      </p>
                    </div>
                  )}
                  
                  <Separator className="my-4" />
                  
                  <div 
                    className="flex items-center justify-between cursor-pointer" 
                    onClick={() => handleToggleExpand(subscription.id)}
                  >
                    <span className="text-sm font-medium">
                      {bookings.length} {t('dashboard.subscriptions.classesBooked')}
                    </span>
                    <Button variant="ghost" size="sm">
                      {isExpanded ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                      }
                    </Button>
                  </div>
                  
                  {isExpanded && (
                    <>
                      {bookings.length > 0 ? (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-4"
                        >
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>{t('classes.details.date')}</TableHead>
                                <TableHead>{t('classes.details.time')}</TableHead>
                                <TableHead>{t('classes.details.class')}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {bookings.map(booking => (
                                <TableRow key={booking.id}>
                                  <TableCell>
                                    {booking.classDate && booking.classDate.length > 0
                                      ? format(parseISO(booking.classDate), 'MMM d, yyyy')
                                      : booking.classDay || t('common.unknownDate')}
                                  </TableCell>
                                  <TableCell>{booking.classTime}</TableCell>
                                  <TableCell>{booking.className}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="mt-4 p-4 bg-muted/30 rounded-lg text-center text-sm text-muted-foreground"
                        >
                          {t('dashboard.subscriptions.noClassesBooked')}
                        </motion.div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      
      {/* Show payment instructions for pending subscriptions */}
      {hasPendingPayments && (
        <div className="mt-6">
          <PaymentInformationSection forSubscriptions={true} />
        </div>
      )}
    </div>
  );
};
